import api from './api';

/**
 * Prediction Service
 * Handles API calls for call outcome predictions
 */
class PredictionService {
  
  /**
   * Generate prediction for a specific call
   * @param {number} callId - Call ID
   * @param {number} timeframe - Analysis timeframe in seconds (default: 30)
   * @returns {Promise} API response
   */
  async generatePrediction(callId, timeframe = 30) {
    try {
      const response = await api.post(`/predictions/${callId}/generate`, {
        timeframe
      });
      return response.data;
    } catch (error) {
      console.error('Error generating prediction:', error);
      throw error;
    }
  }

  /**
   * Get predictions dashboard data
   * @param {Object} filters - Filter parameters
   * @returns {Promise} API response
   */
  async getPredictionsDashboard(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      // Add filter parameters
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.riskLevel) params.append('riskLevel', filters.riskLevel);
      if (filters.agent) params.append('agent', filters.agent);
      if (filters.campaign) params.append('campaign', filters.campaign);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.offset) params.append('offset', filters.offset);

      const response = await api.get(`/predictions/dashboard?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error getting predictions dashboard:', error);
      throw error;
    }
  }

  /**
   * Update prediction with actual outcome
   * @param {number} callId - Call ID
   * @param {string} actualOutcome - Actual call outcome
   * @returns {Promise} API response
   */
  async updateActualOutcome(callId, actualOutcome) {
    try {
      const response = await api.put(`/predictions/${callId}/outcome`, {
        actualOutcome
      });
      return response.data;
    } catch (error) {
      console.error('Error updating actual outcome:', error);
      throw error;
    }
  }

  /**
   * Get prediction analytics
   * @param {Object} params - Analytics parameters
   * @returns {Promise} API response
   */
  async getPredictionAnalytics(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.groupBy) queryParams.append('groupBy', params.groupBy);

      const response = await api.get(`/predictions/analytics?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error getting prediction analytics:', error);
      throw error;
    }
  }

  /**
   * Get risk level color for UI display
   * @param {string} riskLevel - Risk level (high, medium, low)
   * @returns {string} Color code
   */
  getRiskColor(riskLevel) {
    const colors = {
      high: '#f44336',    // Red
      medium: '#ff9800',  // Orange
      low: '#4caf50'      // Green
    };
    return colors[riskLevel] || '#757575'; // Default gray
  }

  /**
   * Get risk level icon
   * @param {string} riskLevel - Risk level (high, medium, low)
   * @returns {string} Icon name
   */
  getRiskIcon(riskLevel) {
    const icons = {
      high: 'warning',
      medium: 'info',
      low: 'check_circle'
    };
    return icons[riskLevel] || 'help';
  }

  /**
   * Format prediction score for display
   * @param {number} score - Prediction score (0-100)
   * @returns {string} Formatted score
   */
  formatScore(score) {
    return `${Math.round(score)}%`;
  }

  /**
   * Format confidence level for display
   * @param {number} confidence - Confidence level (0-1)
   * @returns {string} Formatted confidence
   */
  formatConfidence(confidence) {
    return `${Math.round(confidence * 100)}%`;
  }

  /**
   * Get prediction summary text
   * @param {Object} prediction - Prediction object
   * @returns {string} Summary text
   */
  getPredictionSummary(prediction) {
    const { predictionScore, riskLevel, confidence } = prediction;
    
    if (riskLevel === 'high') {
      return `High risk call (${this.formatScore(predictionScore)}) - Immediate attention needed`;
    } else if (riskLevel === 'medium') {
      return `Medium risk call (${this.formatScore(predictionScore)}) - Monitor closely`;
    } else {
      return `Low risk call (${this.formatScore(predictionScore)}) - Likely positive outcome`;
    }
  }

  /**
   * Get recommendations based on prediction
   * @param {Object} prediction - Prediction object
   * @returns {Array} Array of recommendation strings
   */
  getRecommendations(prediction) {
    const { riskLevel, keyIndicators, recommendations } = prediction;
    
    // Return AI recommendations if available
    if (recommendations && recommendations.immediateActions) {
      return recommendations.immediateActions;
    }

    // Fallback recommendations based on risk level
    const fallbackRecommendations = {
      high: [
        'Consider supervisor intervention',
        'Monitor call in real-time',
        'Prepare escalation procedures',
        'Review agent training needs'
      ],
      medium: [
        'Continue monitoring',
        'Be ready to assist agent',
        'Note key conversation points',
        'Follow up if needed'
      ],
      low: [
        'Standard monitoring',
        'Let agent continue',
        'Document positive practices',
        'Use for training examples'
      ]
    };

    return fallbackRecommendations[riskLevel] || fallbackRecommendations.medium;
  }
}

export default new PredictionService(); 